/**
 * URL Resolution Utilities.
 *
 * Provides helpers for resolving API-provided relative URLs to absolute URLs.
 * This is necessary when the Report Viewer or other tools run on a different
 * origin than the API (e.g., localhost:5173 vs localhost:4000).
 *
 * @see openspec/changes/update-async-report-run-output-parity
 */

/**
 * Resolve a potentially relative URL against an API base URL.
 *
 * The API returns relative URLs (e.g., `/api/v1/reports/exports/file.csv`)
 * for deployment flexibility. When a client runs on a different origin,
 * these must be resolved to absolute URLs before use.
 *
 * @param baseUrl - The API base URL (e.g., 'http://localhost:4000/api/v1')
 * @param relativeOrAbsoluteUrl - The URL to resolve (relative or absolute)
 * @returns An absolute URL pointing to the API host
 *
 * @example
 * ```ts
 * const apiUrl = 'http://localhost:4000/api/v1';
 *
 * // Relative URL is resolved to absolute
 * resolveApiUrl(apiUrl, '/api/v1/reports/exports/file.csv');
 * // => 'http://localhost:4000/api/v1/reports/exports/file.csv'
 *
 * // Already absolute URLs pass through unchanged
 * resolveApiUrl(apiUrl, 'https://cdn.example.com/file.csv');
 * // => 'https://cdn.example.com/file.csv'
 *
 * // Handles trailing slashes in baseUrl
 * resolveApiUrl('http://localhost:4000/api/v1/', '/reports/exports/file.csv');
 * // => 'http://localhost:4000/reports/exports/file.csv'
 * ```
 */
export function resolveApiUrl(baseUrl: string, relativeOrAbsoluteUrl: string): string {
    // If the URL is already absolute, return it unchanged
    if (isAbsoluteUrl(relativeOrAbsoluteUrl)) {
        return relativeOrAbsoluteUrl;
    }

    // Extract the origin from the base URL
    const origin = getOrigin(baseUrl);

    // Resolve the relative path against the origin
    // If the relative URL starts with '/', it's an absolute path from origin
    if (relativeOrAbsoluteUrl.startsWith('/')) {
        return `${origin}${relativeOrAbsoluteUrl}`;
    }

    // For relative paths without leading '/', append to the base URL
    // Remove trailing slash from base URL to avoid double slashes
    const cleanBaseUrl = baseUrl.replace(/\/$/, '');
    return `${cleanBaseUrl}/${relativeOrAbsoluteUrl}`;
}

/**
 * Check if a URL is absolute (has a protocol).
 *
 * @param url - The URL to check
 * @returns true if the URL is absolute
 */
export function isAbsoluteUrl(url: string): boolean {
    return /^https?:\/\//i.test(url);
}

/**
 * Extract the origin (protocol + host + port) from a URL.
 *
 * @param url - The URL to extract the origin from
 * @returns The origin (e.g., 'http://localhost:4000')
 *
 * @example
 * getOrigin('http://localhost:4000/api/v1')
 * // => 'http://localhost:4000'
 */
export function getOrigin(url: string): string {
    try {
        const parsed = new URL(url);
        return parsed.origin;
    } catch {
        // If URL parsing fails, try a regex fallback
        const match = url.match(/^(https?:\/\/[^/]+)/i);
        return match?.[1] || url;
    }
}

